/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TRACING_SYSTEM
 * Date:         2025-12-20
 * Modified By:  Cursor
 *
 * Description:
 * Frontend service for trace API calls.
 */

import axios from 'axios';
import { getAuthHeaders } from '../utils/authUtils';
import { config } from '../config/environment';

const API_URL = config.apiUrl;

export interface TraceEvent {
  timestamp: string;
  service: string;
  event: string;
  message: string;
  metadata?: any;
}

export interface Trace {
  traceId: string;
  userId: string;
  operationType: 'MAIL' | 'UPLOAD' | 'BROADCAST' | 'INVENTORY_BULK' | 'USER_ACTION';
  operationSubtype?: string;
  status: 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'FAILED' | 'PARTIAL';
  parentTraceId?: string;
  description: string;
  metadata?: any;
  progress: {
    current: number;
    total: number;
    percentage: number;
  };
  events: TraceEvent[];
  error?: {
    code: string;
    message: string;
    timestamp: string;
  };
  result?: any;
  timing: {
    startedAt: string;
    completedAt?: string;
    durationMs?: number;
  };
  deviceInfo?: {
    userAgent?: string;
    ip?: string;
    browser?: string;
    browserVersion?: string;
    os?: string;
    osVersion?: string;
    device?: string;
    platform?: string;
    language?: string;
    screenResolution?: string;
    timezone?: string;
  };
  locationInfo?: {
    country?: string;
    countryCode?: string;
    region?: string;
    city?: string;
    timezone?: string;
    isp?: string;
    organization?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface MyTracesParams {
  status?: string;
  type?: string;
  limit?: number;
  page?: number;
}

/**
 * Get user's own traces
 */
export async function getMyTraces(params: MyTracesParams = {}) {
  const response = await axios.get(`${API_URL}/traces/my`, {
    params,
    headers: getAuthHeaders().headers
  });
  return response.data;
}

/**
 * Get specific trace by traceId
 */
export async function getTraceById(traceId: string) {
  const response = await axios.get(`${API_URL}/traces/${traceId}`, {
    headers: getAuthHeaders().headers
  });
  return response.data;
}

/**
 * Get trace events timeline
 */
export async function getTraceEvents(traceId: string) {
  const response = await axios.get(`${API_URL}/traces/${traceId}/events`, {
    headers: getAuthHeaders().headers
  });
  return response.data;
}

/**
 * Poll trace status until complete or failed
 * @param traceId - Trace ID to poll
 * @param onUpdate - Callback on each update
 * @param intervalMs - Polling interval (default: 2000ms)
 * @param maxAttempts - Max polling attempts (default: 60)
 */
export function pollTraceStatus(
  traceId: string,
  onUpdate: (trace: Trace) => void,
  intervalMs: number = 2000,
  maxAttempts: number = 60
): () => void {
  let attempts = 0;
  let intervalId: NodeJS.Timeout | null = null;

  const poll = async () => {
    try {
      attempts++;
      const { data } = await getTraceById(traceId);
      const trace = data.data;

      onUpdate(trace);

      // [2025-12-20] Tracing: Stop polling if terminal status
      if (trace.status === 'SUCCESS' || trace.status === 'FAILED') {
        if (intervalId) clearInterval(intervalId);
        return;
      }

      // [2025-12-20] Tracing: Stop polling if max attempts reached
      if (attempts >= maxAttempts) {
        if (intervalId) clearInterval(intervalId);
        console.warn(`Trace polling stopped: max attempts (${maxAttempts}) reached`);
        return;
      }
    } catch (error) {
      console.error('Trace polling error:', error);
      // Continue polling on error (trace might not exist yet)
    }
  };

  // Start polling immediately, then every intervalMs
  poll();
  intervalId = setInterval(poll, intervalMs);

  // Return cleanup function
  return () => {
    if (intervalId) clearInterval(intervalId);
  };
}

